import {
    AuthFormCard,
    AuthFormCardSubtitle,
    AuthFormCardTitle,
    AuthPageContainer
} from '@features/user/components/auth-form-atoms'
import {Helmet} from 'react-helmet'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {InputText} from '@components/commons/input-text'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {ButtonLink} from '@components/commons/button-link'
import {routes} from '@utilities/constants/routes.tsx'
import {useAsync} from '@hooks/useAsync.ts'
import {httpForgotPassword} from '@features/user/http/user.http.ts'
import {errorHandler} from '@utilities/helpers.ts'
import {useState} from 'react'
import {SuccessModal} from '@components/commons/success-modal'

const ForgotPasswordValidationSchema = z.object({
    email: z.string().min(1, {message: 'errors:required'}).email({message: 'errors:email_invalid'})
})
type ForgotPasswordValidationSchema = z.infer<typeof ForgotPasswordValidationSchema>

export const ForgotPassword = () => {
    const {t} = useTranslation()
    const form = useForm<ForgotPasswordValidationSchema>({resolver: zodResolver(ForgotPasswordValidationSchema)})
    const forgotPassword = useAsync()
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
    const handleSubmit = form.handleSubmit(async formValues => {
        try {
            await forgotPassword.run(httpForgotPassword({payload: formValues}))
            setIsSuccessModalOpen(true)
        } catch (error) {
            errorHandler(error)
        }
    })
    return (
        <AuthPageContainer>
            <Helmet title={t('titles:forgot_password')} />
            {isSuccessModalOpen && (
                <SuccessModal
                    paragraph={t('auth:email_sent')}
                    buttonLabel={t('commons:ok')}
                    onClickCta={() => setIsSuccessModalOpen(false)}
                />
            )}
            <AuthFormCard onSubmit={handleSubmit}>
                <div className="flex flex-col items-center gap-6">
                    <AuthFormCardTitle>{t('commons:forgot_password')}</AuthFormCardTitle>
                    <AuthFormCardSubtitle>{t('auth:enter_email')}</AuthFormCardSubtitle>
                </div>
                <InputText
                    type="text"
                    className="w-full"
                    label={t('commons:email')}
                    errorMessage={t((form.formState.errors.email?.message ?? '') as TKey)}
                    {...form.register('email')}
                />
                <div className="flex flex-col items-center gap-6">
                    <Button size="lg" disabled={forgotPassword.isLoading} className="w-full" type="submit">
                        {t('commons:submit')}
                        {forgotPassword.isLoading && <Spinner />}
                    </Button>
                    <ButtonLink
                        size="xs"
                        to={routes.LOGIN.handle.fullPath}
                        variant="tertiary"
                        disabled={forgotPassword.isLoading}
                    >
                        {t('auth:login')}
                    </ButtonLink>
                </div>
            </AuthFormCard>
        </AuthPageContainer>
    )
}
